/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { ProductDetailsCarousel } from '@/components/blocks/ProductDetails/Carousel';
import { productDetailsImageSX } from '@/components/blocks/ProductDetails/styles/image';
import { ProductImage } from '@/components/blocks/ProductImage';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { isB2BStore, useSettings } from '@/data/Settings';
import { Slide } from '@/data/types/Slide';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { isKitOrBundleType } from '@/utils/productIsA';
import { Box, Grid, Stack, SxProps } from '@mui/material';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { PdpExternalVideoRv } from '@/components/content/PdpExternalVideoRv';
import { Img } from '@/components/blocks/MaterialImage';
import { productDetailBottomLeftRibbonSX, productDetailTopLeftRibbonSX, productDetailTopLeftRibbonSX2, productDetailTopRightRibbonSX } from '@/components/blocks/ProductDetails/styles/carouselMainImage';
import { RibbonImage } from '@/data/types/Product';
import { pdpStructuredData as defaultPdpStructuredData } from '@/data/constants/structuredData';
import { ItnlIntegrationScript } from '@/data/types/ItnlIntegrationScript';
import { magicZoomStartScript } from '@/utils/itnlIntegrationScriptsScripts';
import { loadExternalScripts, removeScripts } from '@/utils/loadExternalScripts';
import { useDeviceType } from '@/data/deviceDetection';
import { useSwipeable } from 'react-swipeable';

export const ProductDetailsGallery: FC<{ sx?: SxProps }> = ({ sx }) => {
	const { settings } = useSettings();
	const { isMobile } = useDeviceType();
	const { selection, product, hasInventory, productAttrValues } = useContext(
		ContentContext
	) as ReturnType<typeof useProductDetails> & ReturnType<typeof useSkuListTable>;

	const [index, setIndex] = useState<number>(0);
	const { maxImages, isB2B } = useMemo(() => ({
			maxImages: parseInt(settings.userData.maxImagesRuby ?? '6'),
			isB2B: isB2BStore(settings),
		}), [settings]);

	const topLeftRibbons:RibbonImage[] =[];
	const topRightRibbons:RibbonImage[] = [];
	const bottomLeftRibbons:RibbonImage[]=[];
	const bottomRightRibbons:RibbonImage[]=[];
	
	const getRibbonImages = (ribbonImages: RibbonImage[]) => {
		ribbonImages.map((ribbon) => {
			// Always fixed and always for exclusief ribbons. 
			if(ribbon.ribbonName =='Exclusief'){
				bottomLeftRibbons.push(ribbon);
			}else
			// Always fixed and always for SVHJ
			if(ribbon.ribbonName =='Speelgoed van het Jaar'){ 
				topRightRibbons.push(ribbon);
			}else {   // All others go here 
				topLeftRibbons.push(ribbon);
			}	
			// For future uses. No Ribbons here as of now. 
			/* if(ribbon.ribbonName ==''){
				bottomRightRibbons.push(ribbon); 
			} */
		} );
	}
	getRibbonImages(product?.ribbonImages??[]);
	 
	const { brands, ean13, yearPublished, colour, material, sku, images, mpn } =
		useMemo(() => {
			const isKitOrBundle = isKitOrBundleType[product?.type ?? 'unknown'];
			return {
				brands: productAttrValues.BrandFacets,
				ean13: productAttrValues.Ean13,
				yearPublished: productAttrValues.OriginalYearOfPublication,
				colour: productAttrValues.Color,
				material: productAttrValues.Material,
				mpn: productAttrValues['Manufacturer part number'],
				sku: !isKitOrBundle ? selection?.sku : product,
				images: product?.images?.map((imag) => imag?.fullImage).filter(Boolean),
				//ribbonData: product?.ribbonImages?.at(0) as RibbonImage,
			};
		}, [
			product,
			productAttrValues.BrandFacets,
			productAttrValues.Color,
			productAttrValues.Ean13,
			productAttrValues.Material,
			productAttrValues.OriginalYearOfPublication,
			selection?.sku,
		]);

	const { name, prices, short } = useMemo(
		() => getProductDisplayInfo(sku, product),
		[sku, product]
	);

	const pdpStructuredData = useMemo(() => {
		if (!product) return null;
		if (images?.length !== undefined)
		for(let i=0;i< (images?.length);i++){
			images[i]=settings.userData.StaticHostSecure+images[i].replaceAll('hclstore', 'wcsstore').replace('wcsstore/wcsstore', 'wcsstore').replace('hclstore/hclstore', 'wcsstore');
		} 
		let tempPdpStructuredDataFinal = defaultPdpStructuredData;
		tempPdpStructuredDataFinal = tempPdpStructuredDataFinal
			.replace('replaceProductName', name)
			.replace('ReplaceImageArray', JSON.stringify(images))
			.replace('replacePartNumber', product?.partNumber ?? '')
			.replace('replaceBrand', brands)
			.replace('replaceMPN', mpn)
			.replace('replaceGTIN13', ean13)
			.replace('replaceColor', colour)
			.replace('replaceMaterial', material)
			.replace('replaceShortDesc', short?.replaceAll('"', '')?.replaceAll('\'', ''))
			.replace('replaceSeoUrl', product?.seo.href ? `${settings.userData.SiteLink_DomainName}${product.seo.href}` : '')
			.replace('replaceAvailability', hasInventory ? 'InStock' : 'OutOfStock');

		if (prices && prices.offer) {
			tempPdpStructuredDataFinal = tempPdpStructuredDataFinal.replace(
				'replacePrice',
				prices.offer + ''
			);
		}else{
			tempPdpStructuredDataFinal = tempPdpStructuredDataFinal.replace(
				'"price":replacePrice,',''
			);
		}

		if(yearPublished){
			tempPdpStructuredDataFinal = tempPdpStructuredDataFinal
			.replace('replaceProductionDate', yearPublished);
		}else{
			tempPdpStructuredDataFinal = tempPdpStructuredDataFinal
			.replace('"productionDate":"replaceProductionDate",', '');
		}
		
		return tempPdpStructuredDataFinal;
	}, [
		brands,
		colour,
		ean13,
		hasInventory,
		images,
		material,
		name,
		prices,
		product,
		short,
		yearPublished,
	]);

	const showCarousel = useMemo(() => (product?.images?.length ?? 0) > 1, [product]);

	const slides1: Slide[] = useMemo(() => (
			product && product.images && product.images.length >= maxImages
				? product.images.slice(0, maxImages)
				: product?.images ?? []
		).map((slide,index) => ({
			...slide,
			thumbnail: slide.thumbnail
				?.replace('hclstore', 'wcsstore')
				.replace('wcsstore/wcsstore', 'wcsstore').replace('hclstore/hclstore', 'wcsstore'),
		})), [maxImages, product]);

	const slides:Slide[] = [];
	slides1.forEach((x) => {
		if(x.fullImage !== undefined) slides.push(x);
	});
	const slidesImage = useMemo(
		() => (isB2B ? slides[0]?.thumbnail : slides[0]?.fullImage),
		[isB2B, slides]
	);

	const skuImage = useMemo(() => (isB2B ? sku?.thumbnail : sku?.fullImage) ?? '', [isB2B, sku]);
	const [src, setSrc] = useState<string>(showCarousel ? slidesImage : skuImage);

	const choose = (i: number) => {
		setSrc(slides[i].fullImage?.replaceAll('hclstore', 'wcsstore').replace('wcsstore/wcsstore', 'wcsstore').replace('hclstore/hclstore', 'wcsstore'));
		setIndex(i);
	};

	useEffect(() => {
		setIndex(0);
		setSrc(
			showCarousel
				? slidesImage?.replaceAll('hclstore', 'wcsstore').replace('wcsstore/wcsstore', 'wcsstore').replace('hclstore/hclstore', 'wcsstore')
				: skuImage?.replaceAll('hclstore', 'wcsstore').replace('wcsstore/wcsstore', 'wcsstore').replace('hclstore/hclstore', 'wcsstore')
		);
	}, [showCarousel, slidesImage, skuImage]);

	const [isMZInit,setMZInit] = useState(false);
	useEffect(()=>{
		const nImage = `${settings.userData.StaticHostSecure}${src}`;
		const mzScript:ItnlIntegrationScript ={ 
			scriptContent:magicZoomStartScript(nImage,nImage,isMZInit), 
			id:'magic_zoom_refresh_script' ,
			delay:100
		}
		if(!isMZInit)setMZInit(true);
		loadExternalScripts([mzScript]);
		return ()=>removeScripts([mzScript]);
	},[src]);

	const swipeMainImage = useSwipeable({
		onSwipedLeft: () => {
			if (!slides || slides.length === 0) return;
			const next = (index + 1) >= slides.length ? 0 : index + 1;
			choose(next);
		},
		onSwipedRight: () => {
			if (!slides || slides.length === 0) return;
			const prev = index === 0 ? (slides.length - 1) : (index - 1);
			choose(prev);
		},
		trackTouch: true,
		trackMouse: false,
		delta: 10,
	});
	return ( 
		<Stack {...(isMobile ? swipeMainImage : {})} id="productImageGrid" rowGap={1.5} position="relative" mb={3}>
			{/** id productImageGrid is used as observee in PdpBazaarVoiceRv widget, if changed here, please change in PdpBazaarVoiceRv widget also **/}
			{topLeftRibbons? (
			<>	<Box sx={productDetailTopLeftRibbonSX}>
					<Img src={topLeftRibbons?.at(0)?.ribbonPath} alt={topLeftRibbons?.at(0)?.ribbonName} isPriority={true}></Img>
				</Box> 
			{/*  {topLeftRibbons?.at(1)?	(<Box sx={productDetailTopLeftRibbonSX2}>
					<Img src={topLeftRibbons?.at(1)?.ribbonPath} alt={topLeftRibbons?.at(1)?.ribbonName} isPriority={true}></Img>
				</Box>):null } */}
			</>
			) : null} 
			{/* {topRightRibbons? (
				<Box sx={productDetailTopRightRibbonSX}>
					<Img src={topRightRibbons?.at(0)?.ribbonPath} alt={topRightRibbons?.at(0)?.ribbonName} isPriority={true}></Img>
				</Box>
			) : null} 
			{bottomLeftRibbons? (
				<Box sx={productDetailBottomLeftRibbonSX}>
					<Img src={bottomLeftRibbons?.at(0)?.ribbonPath} alt={bottomLeftRibbons?.at(0)?.ribbonName} isPriority={true}></Img>
				</Box>
			) : null} */}
			{showCarousel ? (
				<ProductDetailsCarousel {...{ slides, choose, index, src, alt: name, sx }} />
			) : (
				<Grid container>
					<Grid item xs={12} sx={productDetailsImageSX}>
						<ProductImage {...{ src, alt: name }} />
					</Grid>
				</Grid>
			)}

			{product ? <PdpExternalVideoRv id={product.partNumber} /> : null}

			{pdpStructuredData ? (
				<script
					id="productData"
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: pdpStructuredData }}
				></script>
			) : null}
		</Stack>
	);
};
